import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';

const SliderContainer = styled.div`
  position: relative;
  top: 1px;
  width: 100%;
  height: 4px;
  background-color: #364856;
  cursor: pointer;
  &::after {
    content: '';
    position: absolute;
    width: 4px;
    height: 4px;
    background-color: #364856;
    border-radius: 50%;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
`;

const SliderThumb = styled.div`
  position: absolute;
  width: 15px;
  height: 15px;
  background-color: #364856;
  border-radius: 50%;
  cursor: grab;
  transform: translate(-50%, -50%);
  top: 2px;
`;

export interface DraggableSliderProps {
  value: number;
  setValue: (v: number) => void;
}

const DraggableSlider = ({ value, setValue }: DraggableSliderProps) => {
  const sliderRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [transition, setTransition] = useState(0);

  const handleMouseDown = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => {
    setIsDragging(true);
    updateValue(event);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseMove = (event: MouseEvent) => {
    if (isDragging) {
      updateValue(event);
    }
  };

  const updateValue = (
    event: MouseEvent | React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => {
    if (sliderRef.current) {
      const sliderRect = sliderRef.current.getBoundingClientRect();
      const newPosition = Math.min(
        Math.max((event.clientX - sliderRect.left) / sliderRect.width, 0),
        1,
      );

      if (Math.abs(newPosition - 0.5) < 0.03) {
        setValue(0.5);
      } else {
        setValue(newPosition);
      }

      if (Math.abs(newPosition - 0.5) < 0.06) {
        setTransition(300);
      } else {
        setTransition(0);
      }
    }
  };

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  return (
    <SliderContainer onMouseDown={handleMouseDown} ref={sliderRef}>
      <SliderThumb
        style={{
          transition: `left ${transition}ms`,
          left: `${value * 100}%`,
        }}
      />
    </SliderContainer>
  );
};

export default DraggableSlider;
